import { TimePointV1 } from '../calendar/time-point.js';
import { ClientRecordOrigin, DiscoveryState, KnowledgeAction, PlayRecordState, RecordOrigin, SessionOrigin, SessionStatus, Tick, } from '../domain/enums.js';
import { z } from '../zod.js';
import { DatedPointV1, PlacementV1 } from './chronology.js';
import { Fid, IsoTimestamp, RowVersion, Uuid } from './common.js';
import { DocTitle } from './documents.js';
import { DocRefV1 } from './links.js';
import { PageQueryV1 } from './list.js';
/**
 * The ledger and sessions of exit 1b (1.0b deliverable 20): secrets and what the table learned
 * (1.M-1), play records (1.M-1) and sessions (1.T). Every record names a session, or is marked
 * as before tracking began.
 */
const Text = z.string().max(10_000);
const has = (value) => value !== undefined && value !== null;
/**
 * A secret (`secret`): its content derived from the body that holds it, its state from the
 * knowledge events. `orphanedAt` is set once its block left every body.
 */
export const SecretV1 = z.strictObject({
    id: Uuid,
    fid: Fid,
    rv: RowVersion,
    documentId: Uuid,
    blockId: Uuid.nullable(),
    label: z.string().max(500).nullable(),
    text: Text,
    trigger: z.string().max(500).nullable(),
    orphanedAt: IsoTimestamp.nullable(),
    retiredAt: IsoTimestamp.nullable(),
    state: DiscoveryState,
    firstKnownSessionId: Uuid.nullable(),
    knownBeforeTracking: z.boolean(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
});
/** `secrets.list`: every secret, or those in one state, paged. */
export const SecretsQueryV1 = z.strictObject({
    ...PageQueryV1.shape,
    state: DiscoveryState.optional(),
});
/** Baseline section 3's checks on `knowledge_event`, for an event or a create. */
function knowledgeRule(k, ctx) {
    if (has(k.secretId) === has(k.documentId)) {
        ctx.addIssue({ code: 'custom', message: 'a secret or a document', path: ['secretId'] });
    }
    if ((k.action ?? 'assert') === 'assert' ? !has(k.state) : has(k.state)) {
        ctx.addIssue({ code: 'custom', message: 'an assertion has a state', path: ['state'] });
    }
    recordRule(k, ctx);
}
/** A record names its session, or is marked as before tracking began. */
function recordRule(r, ctx) {
    if (r.beforeTracking !== true && !has(r.sessionId)) {
        ctx.addIssue({ code: 'custom', message: 'a session, or before tracking', path: ['sessionId'] });
    }
}
/** What the players learned of a secret or a document (`knowledge_event`). */
export const KnowledgeEventV1 = z
    .strictObject({
    id: Uuid,
    secretId: Uuid.nullable(),
    documentId: Uuid.nullable(),
    action: KnowledgeAction,
    state: DiscoveryState.nullable(),
    tentative: z.boolean(),
    supersedesId: Uuid.nullable(),
    sessionId: Uuid.nullable(),
    beforeTracking: z.boolean(),
    at: DatedPointV1.nullable(),
    pcIds: z.array(Uuid),
    how: z.string().max(2000).nullable(),
    origin: RecordOrigin,
    foldSessionNo: z.number().int().nonnegative().nullable(),
    foldTick: Tick.nullable(),
    createdAt: IsoTimestamp,
})
    .superRefine(knowledgeRule);
/** `knowledge.create`: an assertion (the default) with its state, or a retraction. */
export const KnowledgeCreateV1 = z
    .strictObject({
    secretId: Uuid.optional(),
    documentId: Uuid.optional(),
    action: KnowledgeAction.optional(),
    state: DiscoveryState.optional(),
    tentative: z.boolean().optional(),
    supersedesId: Uuid.optional(),
    sessionId: Uuid.optional(),
    beforeTracking: z.boolean().optional(),
    at: TimePointV1.optional(),
    pcIds: z.array(Uuid).max(50).optional(),
    how: z.string().max(2000).optional(),
    /**
     * CS r5a deliverable 143: `run_mode` from Run mode's reveals, else `manual` (the default); the
     * server writes every other origin (open point 35).
     */
    origin: ClientRecordOrigin.optional(),
})
    .superRefine(knowledgeRule);
/** Whether a document or one of its scenes was played (`play_record`). */
export const PlayRecordV1 = z
    .strictObject({
    id: Uuid,
    /**
     * Null only for a record of a session item that names no canon document: a `note` item, or an
     * item on a reference document (CS r5a deliverable 143).
     */
    documentId: Uuid.nullable(),
    blockId: Uuid.nullable(),
    /**
     * The run-sheet item the mark was made on (CS r5a deliverable 143), null for a mark on a
     * document. Optional so a server built before 1.M-1 stage s3 still answers within the schema;
     * from s3 it is always present.
     */
    sessionItemId: Uuid.nullable().optional(),
    sessionId: Uuid.nullable(),
    beforeTracking: z.boolean(),
    state: PlayRecordState,
    outcomeNote: Text.nullable(),
    supersedesId: Uuid.nullable(),
    origin: RecordOrigin,
    foldSessionNo: z.number().int().nonnegative().nullable(),
    createdAt: IsoTimestamp,
})
    .superRefine(recordRule);
/**
 * `play.create`: a document, or one scene of it by its block, or a run-sheet item (CS r5a
 * deliverable 143), or an item and its document. A mark on an item takes the item's `document_id`
 * and `block_id`, except that `document_id` stays null when the item names a reference document;
 * a `documentId` sent in the body keeps the route's `deny` (a reference id answers 403). A block
 * belongs to a document, so `blockId` comes with `documentId`. `origin` is `manual` (the default)
 * or `run_mode`; the server writes every other origin, `undo` included (open points 34 and 35).
 */
export const PlayCreateV1 = z
    .strictObject({
    documentId: Uuid.optional(),
    blockId: Uuid.optional(),
    sessionItemId: Uuid.optional(),
    sessionId: Uuid.optional(),
    beforeTracking: z.boolean().optional(),
    state: PlayRecordState,
    outcomeNote: Text.optional(),
    supersedesId: Uuid.optional(),
    origin: ClientRecordOrigin.optional(),
})
    .superRefine((p, ctx) => {
    if (p.documentId === undefined && p.sessionItemId === undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'a document, a session item, or both',
            path: ['documentId'],
        });
    }
    if (p.blockId !== undefined && p.documentId === undefined) {
        ctx.addIssue({ code: 'custom', message: 'a block of a document', path: ['blockId'] });
    }
    recordRule(p, ctx);
});
/** A real-world calendar date, `YYYY-MM-DD`. */
const RealDate = z.iso.date();
/**
 * A session (a document of the built-in `session` type with its `session_meta`) and its in-world
 * span, the placement with role `session`: `sessions.create` writes an undated one through 1.L-2
 * (CS r2c, 1.T item T2); `placement` is null when the session has no live placement with role
 * `session`.
 */
export const SessionV1 = z
    .strictObject({
    document: DocRefV1,
    number: z.number().int().min(1).nullable(),
    realDate: RealDate.nullable(),
    status: SessionStatus,
    origin: SessionOrigin,
    startedAt: IsoTimestamp.nullable(),
    endedAt: IsoTimestamp.nullable(),
    placement: PlacementV1.nullable(),
})
    .refine((s) => ['planned', 'prepped'].includes(s.status) || s.number !== null, {
    message: 'a session past prep has its number',
    path: ['number'],
});
/**
 * `sessions.create`: minimal session creation (behind the `ledger` flag). The rows refuse with 422
 * `invalid_body` (CS r2c, 1.T item T1): a number any session already holds, trashed or merged
 * included; a status past `prepped` without a number; status `running`; a title the database cannot
 * store.
 */
export const SessionCreateV1 = z.strictObject({
    title: DocTitle,
    number: z.number().int().min(1).optional(),
    realDate: RealDate.optional(),
    status: SessionStatus.optional(),
});
/**
 * `sessions.update` (CS r2c, 1.T item T4): the number, the real date and the status, each optional,
 * the first two clearable with null. No `baseRv`: the last write wins. `running` is refused here
 * (Phase 2's run mode sets it); a number another session holds, or a status past `prepped` without
 * a number, answers 422 `invalid_body` from the rows.
 */
export const SessionUpdateV1 = z.strictObject({
    number: z.number().int().min(1).nullable().optional(),
    realDate: RealDate.nullable().optional(),
    status: SessionStatus.exclude(['running']).optional(),
});
//# sourceMappingURL=ledger.js.map